import { PARENT_CHILD_REFERENCE } from '@ardoq/global-consts';
import type { GetDistinctReferenceKeyArgs } from './types';
import { omit } from 'lodash';

/**
 * Generates a unique key for aggregating references in graph visualizations.
 * Two references will only aggregate if ALL their key components match.
 *
 * **IMPORTANT**: Pay close attention to which optional arguments are provided at the call site,
 * as they directly affect how references are aggregated. Only non-empty optional parameters
 * are included in the generated key.
 *
 * For parent-child references, only source and target node IDs are used for the key,
 * ignoring all other parameters.
 *
 * @param args - Configuration for generating the reference key
 * @param args.globalTypeId - The global type identifier of the reference
 * @param args.label - The primary label of the reference
 * @param args.sourceGraphNodeId - ID of the source graph node
 * @param args.targetGraphNodeId - ID of the target graph node
 * @param args.isParentChildReference - Whether this is a parent-child relationship
 * @param args.diffState - The diff state of the reference
 * @param args.lineColor - Optional line color; always included in key (even if undefined)
 * @param args.urlFieldValues - Optional URL field values; included only if present
 * @param args.otherLabels - Optional additional labels; included only if present and non-empty
 *
 * @returns A string key used to aggregate references
 */
export const getDistinctReferenceKey = ({
  globalTypeId,
  label,
  sourceGraphNodeId,
  targetGraphNodeId,
  isParentChildReference,
  diffState,
  lineColor,
  urlFieldValues,
  otherLabels,
}: GetDistinctReferenceKeyArgs): string => {
  if (isParentChildReference) {
    return `${PARENT_CHILD_REFERENCE}-${sourceGraphNodeId}-${targetGraphNodeId}`;
  }

  const urlFieldsPart = urlFieldValues
    ?.filter(({ url }) => !!url)
    .map(({ url, label }) => `${url}:${label}`)
    .join(',');

  const otherLabelsPart =
    otherLabels && otherLabels.length > 0
      ? `,${otherLabels.flatMap(label => label).join(',')}`
      : '';

  return `${globalTypeId},${sourceGraphNodeId},${targetGraphNodeId},${label}${urlFieldsPart},${diffState},${lineColor}${otherLabelsPart}`;
};

/**
 * This function disregards otherLabels for the reference aggregation,
 * even if otherLabels are present
 */
export const legacyGetDistinctReferenceKey = (
  args: GetDistinctReferenceKeyArgs
) => getDistinctReferenceKey(omit(args, 'otherLabels'));
